import { useState, useEffect, useCallback, Fragment } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { toast } from 'sonner';
import { DownloadSimple, ArrowsClockwise, CaretRight, CaretDown } from '@phosphor-icons/react';

const money = (v) => (v ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '–');
const fmtD = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '');
const today = () => new Date().toISOString().slice(0, 10);
const TABS = [{ key: 'daybook', label: 'Day Book' }, { key: 'ar', label: 'AR Ageing (Receivables)' }, { key: 'ap', label: 'AP Ageing (Payables)' }];

async function downloadBlob(url, params, fname) {
  const resp = await api.get(url, { params, responseType: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([resp.data]));
  link.download = fname;
  link.click();
}

function useLocations() {
  const [locs, setLocs] = useState([]);
  useEffect(() => {
    api.get('/billing/admin/branches').then(r => setLocs((r.data.branches || []).map(b => b.name))).catch(() => {});
  }, []);
  return locs.map(l => ({ value: l, label: l }));
}

function DayBook({ locOptions }) {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [types, setTypes] = useState([]);
  const [locs, setLocs] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = useCallback(() => ({ start_date: from, end_date: to, voucher_types: types.join(','), location: locs.join(',') }), [from, to, types, locs]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/reports/accounting/day-book', { params: { ...params(), page: p, page_size: 100 } });
      setData(d); setPage(p);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load Day Book'); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div className="space-y-4" data-testid="daybook-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div><label className="text-[10px] text-[#64748B] block mb-1">FROM</label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="db-from" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">TO</label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs w-36" data-testid="db-to" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">VOUCHER TYPE</label>
          <MultiSelectFilter value={types} onChange={setTypes} options={data?.voucher_types || []} allLabel="All Vouchers" noun="types" testid="db-types" buttonClassName="h-8 text-xs w-44" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">LOCATION</label>
          <MultiSelectFilter value={locs} onChange={setLocs} options={locOptions} allLabel="All Locations" noun="locations" testid="db-locations" buttonClassName="h-8 text-xs w-52" /></div>
        <Button size="sm" onClick={() => load(1)} className="h-8 text-xs bg-[#0F4C81] text-white" data-testid="db-apply"><ArrowsClockwise size={12} className="mr-1" /> Apply</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="db-export"
          onClick={() => downloadBlob('/reports/accounting/day-book/export', params(), `day_book_${from}_to_${to}.xlsx`).catch(() => toast.error('Export failed'))}>
          <DownloadSimple size={12} className="mr-1" /> Export Excel</Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="db-type-totals">
          {Object.entries(data.by_type).map(([k, t]) => (
            <div key={k} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`db-total-${k}`}>
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">{t.label}</p>
              <p className="text-sm font-semibold text-[#0F172A]">{money(t.amount)}</p>
              <p className="text-[10px] text-[#94A3B8]">{t.count} voucher(s)</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
        <table className="w-full text-xs" data-testid="db-table">
          <thead className="bg-[#0F4C81] text-white"><tr>
            {['Date', 'Voucher Type', 'Voucher #', 'Party / Particulars', 'Location', 'Mode'].map(h => <th key={h} className="text-left p-2.5 font-medium">{h}</th>)}
            <th className="text-right p-2.5 font-medium">Debit</th><th className="text-right p-2.5 font-medium">Credit</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-[#64748B]">Loading…</td></tr>}
            {!loading && data?.rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#64748B]">No vouchers in this period</td></tr>}
            {!loading && data?.rows.map((r, i) => (
              <tr key={`${r.voucher_type}-${r.number}-${i}`} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`db-row-${i}`}>
                <td className="p-2.5 whitespace-nowrap">{fmtD(r.date)}</td>
                <td className="p-2.5">{r.voucher_label}</td>
                <td className="p-2.5 font-mono">{r.number}</td>
                <td className="p-2.5">{r.party}</td>
                <td className="p-2.5 text-[#64748B]">{r.location}</td>
                <td className="p-2.5 text-[#64748B] capitalize">{r.mode}</td>
                <td className="p-2.5 text-right">{r.debit ? money(r.debit) : ''}</td>
                <td className="p-2.5 text-right">{r.credit ? money(r.credit) : ''}</td>
              </tr>
            ))}
          </tbody>
          {data && (
            <tfoot className="bg-[#F1F5F9] font-semibold"><tr>
              <td colSpan={6} className="p-2.5">Total ({data.total} vouchers)</td>
              <td className="p-2.5 text-right" data-testid="db-total-debit">{money(data.total_debit)}</td>
              <td className="p-2.5 text-right" data-testid="db-total-credit">{money(data.total_credit)}</td>
            </tr></tfoot>
          )}
        </table>
      </div>
      {data && pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="db-prev">Prev</Button>
          <span data-testid="db-page">Page {page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => load(page + 1)} data-testid="db-next">Next</Button>
        </div>
      )}
    </div>
  );
}

function Ageing({ kind, locOptions }) {
  const [asOf, setAsOf] = useState(today());
  const [locs, setLocs] = useState([]);
  const [search, setSearch] = useState('');
  const [inter, setInter] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const partyLabel = kind === 'ar' ? 'Customer' : 'Vendor';
  const params = useCallback(() => ({ as_of: asOf, location: locs.join(','), search, include_inter_branch: inter }), [asOf, locs, search, inter]);

  const load = useCallback(async () => {
    setLoading(true); setOpen({});
    try { const { data: d } = await api.get(`/reports/accounting/ageing/${kind}`, { params: params() }); setData(d); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load ageing'); }
    finally { setLoading(false); }
  }, [kind, params]);
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (party) => {
    if (open[party]) { setOpen(o => ({ ...o, [party]: null })); return; }
    try {
      const { data: d } = await api.get(`/reports/accounting/ageing/${kind}`, { params: { ...params(), party } });
      setOpen(o => ({ ...o, [party]: d.details || [] }));
    } catch { toast.error('Failed to load details'); }
  };

  const buckets = data?.buckets || [];
  return (
    <div className="space-y-4" data-testid={`${kind}-ageing-tab`}>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div><label className="text-[10px] text-[#64748B] block mb-1">AS OF</label><Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="h-8 text-xs w-36" data-testid={`${kind}-asof`} /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">LOCATION</label>
          <MultiSelectFilter value={locs} onChange={setLocs} options={locOptions} allLabel="All Locations" noun="locations" testid={`${kind}-locations`} buttonClassName="h-8 text-xs w-52" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">{partyLabel.toUpperCase()} / DOC #</label>
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search" className="h-8 text-xs w-44" data-testid={`${kind}-search`} /></div>
        <label className="flex items-center gap-2 text-xs cursor-pointer h-8" data-testid={`${kind}-inter-toggle`}>
          <input type="checkbox" checked={inter} onChange={e => setInter(e.target.checked)} className="accent-[#0F4C81]" />
          Include inter-branch (HearClear) {kind === 'ar' ? 'customers' : 'vendors'}
        </label>
        <Button size="sm" onClick={load} className="h-8 text-xs bg-[#0F4C81] text-white" data-testid={`${kind}-apply`}><ArrowsClockwise size={12} className="mr-1" /> Apply</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`${kind}-export`}
          onClick={() => downloadBlob(`/reports/accounting/ageing/${kind}/export`, params(), `${kind}_ageing_${asOf}.xlsx`).catch(() => toast.error('Export failed'))}>
          <DownloadSimple size={12} className="mr-1" /> Export Excel</Button>
      </div>
      <p className="text-[11px] text-[#64748B]">Days are counted from the due date (invoice/bill date when no due date) up to the As-of date, on the current outstanding balance.</p>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid={`${kind}-bucket-totals`}>
          <div className="bg-[#0F4C81] text-white rounded-lg p-3"><p className="text-[10px] uppercase tracking-wide opacity-80">Total Outstanding</p>
            <p className="text-sm font-semibold" data-testid={`${kind}-grand-total`}>{money(data.totals.total)}</p><p className="text-[10px] opacity-80">{data.totals.count} document(s)</p></div>
          {buckets.map(b => (
            <div key={b.key} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`${kind}-bucket-${b.key}`}>
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">{b.label}</p>
              <p className={`text-sm font-semibold ${b.key === 'b180p' && data.totals[b.key] ? 'text-red-600' : 'text-[#0F172A]'}`}>{money(data.totals[b.key])}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
        <table className="w-full text-xs" data-testid={`${kind}-table`}>
          <thead className="bg-[#0F4C81] text-white"><tr>
            <th className="text-left p-2.5 font-medium">{partyLabel}</th><th className="text-right p-2.5 font-medium">Docs</th>
            {buckets.map(b => <th key={b.key} className="text-right p-2.5 font-medium">{b.label}</th>)}
            <th className="text-right p-2.5 font-medium">Total</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-[#64748B]">Loading…</td></tr>}
            {!loading && data?.rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[#64748B]">Nothing outstanding</td></tr>}
            {!loading && data?.rows.map((r, i) => (
              <Fragment key={r.party}>
                <tr className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer" onClick={() => toggle(r.party)} data-testid={`${kind}-row-${i}`}>
                  <td className="p-2.5 font-medium text-[#0F4C81]"><span className="inline-flex items-center gap-1">{open[r.party] ? <CaretDown size={10} /> : <CaretRight size={10} />}{r.party}</span></td>
                  <td className="p-2.5 text-right">{r.count}</td>
                  {buckets.map(b => <td key={b.key} className="p-2.5 text-right">{money(r[b.key])}</td>)}
                  <td className="p-2.5 text-right font-semibold">{money(r.total)}</td>
                </tr>
                {open[r.party] && (
                  <tr className="bg-[#F8FAFC]"><td colSpan={9} className="p-2">
                    <table className="w-full text-[11px]" data-testid={`${kind}-details-${i}`}>
                      <thead className="text-[#64748B]"><tr>
                        {[kind === 'ar' ? 'Invoice #' : 'Bill #', 'Date', 'Due Date', 'Days Overdue', 'Location'].map(h => <th key={h} className="text-left p-1.5 font-medium">{h}</th>)}
                        <th className="text-right p-1.5 font-medium">Amount</th><th className="text-right p-1.5 font-medium">Balance</th>
                      </tr></thead>
                      <tbody>{open[r.party].map(d => (
                        <tr key={d.number + d.date} className="border-t border-[#E2E8F0]">
                          <td className="p-1.5 font-mono">{d.number}</td><td className="p-1.5">{fmtD(d.date)}</td><td className="p-1.5">{fmtD(d.due_date)}</td>
                          <td className={`p-1.5 ${d.days_overdue > 90 ? 'text-red-600 font-medium' : ''}`}>{d.days_overdue}</td><td className="p-1.5">{d.location}</td>
                          <td className="p-1.5 text-right">{money(d.amount)}</td><td className="p-1.5 text-right font-medium">{money(d.balance)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AccountingReportsPage() {
  const [tab, setTab] = useState('daybook');
  const locOptions = useLocations();
  return (
    <div className="space-y-5" data-testid="accounting-reports-page">
      <PageHeader title="Accounting Reports" subtitle="Day Book and Receivable / Payable ageing built from invoices, receipts, credit notes, bills and payments" />
      <div className="flex gap-1 border-b border-[#E2E8F0]">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`acc-tab-${t.key}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === t.key ? 'border-[#0F4C81] text-[#0F4C81] font-semibold' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'daybook' && <DayBook locOptions={locOptions} />}
      {tab === 'ar' && <Ageing key="ar" kind="ar" locOptions={locOptions} />}
      {tab === 'ap' && <Ageing key="ap" kind="ap" locOptions={locOptions} />}
    </div>
  );
}
